// notificationService.js
import { getMessaging, onMessage, getToken, deleteToken } from 'firebase/messaging';
import { initializeApp } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';
import { authedJsonHeaders, ENV_SUFFIX, messaging } from '../firebase';

// Notification sending function
// The endpoint refuses an unauthenticated call, and only lets the caller send
// to topics their own organisation, incidents and chats actually reach.
export const sendNotificationToTopic = async (notificationData) => {
  try {
    const headers = await authedJsonHeaders();
    if (!headers) {
      throw new Error('Not signed in');
    }

    const response = await fetch(
      `https://us-central1-live-alarm-v2.cloudfunctions.net/sendToTopic${ENV_SUFFIX}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(notificationData),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending notification:', error);
    throw error;
  }
};

// Subscribe to a topic
export const subscribeToTopic = async (topic) => {
  try {
    const currentToken = await getToken(messaging, { vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY });

    if (currentToken) {
      const response = await fetch(
        'https://iid.googleapis.com/iid/v1/' + currentToken + '/rel/topics/' + topic,
        {
          method: 'POST',
          headers: {
            'Authorization': 'key=' + 'YOUR_SERVER_KEY',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to subscribe to topic: ${response.status}`);
      }

      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error('Error subscribing to topic:', error);
    return false;
  }
};

// Unsubscribe from a topic
export const unsubscribeFromTopic = async (topic) => {
  try {
    const currentToken = await getToken(messaging, { vapidKey: 'YOUR_VAPID_KEY' });

    if (currentToken) {
      // Note: Firebase doesn't have a direct unsubscribe API for topics
      // You might need to implement this differently based on your needs
      console.log(`Unsubscribe functionality would be implemented here for topic: ${topic}`);
      return true;
    } else {
      console.log('No registration token available.');
      return false;
    }
  } catch (error) {
    console.error('Error unsubscribing from topic:', error);
    return false;
  }
};

// Subscribe user to all required topics based on their profile and roles
export const subscribeUserToAllTopics = async (user) => {
  if (!user || !user.id) return;

  const topics = [
    'livealarm-common',
    `livealarm-chat-common-${user.id}`,
    `livealarm-user-${user.id}`
  ];

  // Add organization topics if user has organizations
  if (user.organizations && user.organizations.length > 0) {
    user.organizations.forEach(org => {
      topics.push(`livealarm-organization-${org.id}`);

      // Add duty-specific topics based on user's duty status
      if (org.dutyStatus === 'on-duty') {
        topics.push(`livealarm-organization-${org.id}-on-duty`);
      } else if (org.dutyStatus === 'off-duty') {
        topics.push(`livealarm-organization-${org.id}-off-duty`);
      }
    });
  }

  // Subscribe to all topics
  for (const topic of topics) {
    await subscribeToTopic(topic);
  }

};

// Unsubscribe user from all organization duty topics when duty status changes
export const updateDutyTopics = async (user, orgId, newDutyStatus) => {
  if (!user || !user.id || !orgId) return;

  // Unsubscribe from both duty topics
  await unsubscribeFromTopic(`livealarm-organization-${orgId}-on-duty`);
  await unsubscribeFromTopic(`livealarm-organization-${orgId}-off-duty`);

  // Subscribe to the new duty topic
  await subscribeToTopic(`livealarm-organization-${orgId}-${newDutyStatus}`);

};

// Handle incoming messages when the app is in the foreground
export const setupForegroundMessageHandler = () => {
  onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);
    // Handle the received message here (show notification, update UI, etc.)
  });
};

// Request notification permission
export const requestNotificationPermission = async () => {
  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      console.log('Notification permission granted.');
      return true;
    } else {
      console.log('Unable to get permission to notify.');
      return false;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Get FCM token
export const getFCMToken = async () => {
  try {
    const currentToken = await getToken(messaging, { vapidKey: 'YOUR_VAPID_KEY' });
    if (currentToken) {
      console.log('FCM Token:', currentToken);
      return currentToken;
    } else {
      console.log('No registration token available.');
      return null;
    }
  } catch (error) {
    console.error('An error occurred while retrieving token:', error);
    return null;
  }
};

// Delete FCM token (for logout)
export const deleteFCMToken = async () => {
  try {
    await deleteToken(messaging);
    console.log('Token deleted.');
  } catch (error) {
    console.error('Error deleting token:', error);
  }
};

// Helper function to send new incident notification
export const sendNewIncidentNotification = async (incident, organization, isOffDuty = false) => {
  const topic = isOffDuty
    ? `${process.env.REACT_APP_ENV === 'production' ? 'p' : ''}livealarm-organization-${organization.id}-off-duty`
    : `${process.env.REACT_APP_ENV === 'production' ? 'p' : ''}livealarm-organization-${organization.id}-on-duty`;

  const notificationData = {
    topic,
    title: "New Incident",
    body: incident.description || "New incident reported",
    data: {
      type: "new_incident",
      incidentId: incident.id
    }
  };

  try {
    await sendNotificationToTopic(notificationData);
    console.log(`Notification sent to ${topic}`);
    return true;
  } catch (error) {
    console.error(`Failed to send notification to ${topic}:`, error);
    return false;
  }
};

export default {
  sendNotificationToTopic,
  subscribeToTopic,
  unsubscribeFromTopic,
  subscribeUserToAllTopics,
  updateDutyTopics,
  setupForegroundMessageHandler,
  requestNotificationPermission,
  getFCMToken,
  deleteFCMToken,
  sendNewIncidentNotification
};